import React from 'react';
import {
  LayoutDashboard,
  Database,
  BarChart3,
  Users,
  Settings,
  ShieldCheck,
  Building2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { Badge } from '../common/Badge';

export interface SidebarProps {
  currentView: string;
  onSelectView: (view: string) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentView, onSelectView }) => {
  const { tenant } = useAuth();
  const { subdomain } = useTenant();

  const navItems = [
    { id: 'dashboard', label: 'Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: 'records', label: 'Tenant Records', icon: <Database className="w-4 h-4" /> },
    { id: 'analytics', label: 'Central Telemetry', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'members', label: 'Organization Team', icon: <Users className="w-4 h-4" /> },
    { id: 'settings', label: 'Workspace Settings', icon: <Settings className="w-4 h-4" /> },
  ];

  return (
    <aside
      className="glass-panel"
      style={{
        margin: '16px',
        padding: '24px 16px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: 'var(--radius-xl)',
        height: 'calc(100vh - 32px)',
      }}
    >
      <div>
        {/* Organization Brand Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 8px 24px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'var(--brand-gradient)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: 'var(--brand-glow)',
              flexShrink: 0,
            }}
          >
            <Building2 className="w-5 h-5" />
          </div>
          <div style={{ overflow: 'hidden' }}>
            <h3 style={{ fontSize: '1rem', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
              {tenant?.name || subdomain}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {subdomain}.saas
              </span>
              <Badge variant="primary" style={{ fontSize: '0.65rem', padding: '1px 5px' }}>
                {tenant?.tier || 'Pro'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <nav style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {navItems.map((item) => {
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onSelectView(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: isActive ? 'var(--brand-primary-light)' : 'transparent',
                  border: isActive ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent',
                  color: isActive ? '#a5b4fc' : 'var(--text-secondary)',
                  fontWeight: isActive ? 600 : 500,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <span style={{ color: isActive ? 'var(--brand-primary)' : 'var(--text-muted)' }}>
                  {item.icon}
                </span>
                {item.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Database Isolation Status Box */}
      <div
        style={{
          background: 'rgba(15, 23, 42, 0.7)',
          padding: '14px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          fontSize: '0.75rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-success)', fontWeight: 600, marginBottom: '6px' }}>
          <ShieldCheck className="w-4 h-4" />
          <span>Strict DB Isolation</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '8px', lineHeight: 1.4 }}>
          Tenant database pool:
        </p>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.7rem',
            background: 'rgba(0, 0, 0, 0.3)',
            padding: '4px 8px',
            borderRadius: '4px',
            color: '#a5b4fc',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          tenant_{subdomain}_db
        </div>
      </div>
    </aside>
  );
};
